<script lang="ts">
  /**
   * The estate: what is running, how it is scaled, what it costs, what changed,
   * and what Google is trying to tell you.
   *
   * Every panel loads independently and fails independently. One API being
   * disabled or throttled must not blank the screen — a half-answered admin
   * page is far more useful than an error page.
   *
   * Figures that are not wired render as `—`, never as a plausible invented
   * number. `/cost` says so in its own words when billing export is not enabled,
   * and that sentence is shown rather than replaced with a zero.
   */
  import { Icon, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { stepUp } from "../lib/stepup.svelte";

  type Service = {
    id: string;
    project: string;
    region: string;
    uri: string | null;
    minInstances: number;
    maxInstances: number | null;
    latestRevision: string | null;
    updatedAt: string | null;
    ready: boolean;
    portalLabel: string | null;
    serving: { revision: string; percent: number }[];
  };
  type PortalRow = { id: string; name: string; url: string; projectId: string; status: string; enabled: boolean };
  type Notice = { id?: string; title: string; state: string | null; category: string | null; updateTime: string | null };
  type Rec = { recommender: string; description: string; priority: string | null };
  type Release = { version: string; at: string | null; type: string | null; by: string | null; files: string | null; sha: string | null; subject: string | null; runUrl: string | null };
  type SiteReleases = { portalId: string; name: string; site: string; releases: Release[] };
  type ErrRow = { project: string; errors: number; lastAt: string | null };
  type Perf = { id: string; requests: number | null; p50: number | null; p95: number | null; coldStarts: number | null };
  type CostGroup = { name: string; monthToDate: number; projected: number | null; share: number };
  type Cost = {
    monthToDate: number | null;
    projectedMonthEnd: number | null;
    projectable: boolean;
    budget: number;
    overBudget: boolean;
    currency: string;
    dayOfMonth?: number;
    daysInMonth?: number;
    byProject: CostGroup[];
    byPortal: { portal: string; cost: number; projected: number | null; share: number }[];
    byService: CostGroup[];
    daily: { day: string; cost: number }[];
    rows: { project: string; portal: string; service: string; day: string; cost: number }[];
    note?: string;
  };
  type Limit = { id: string; label: string; used: number | null; limit: number | null; unit: string; note: string; pct: number | null };

  type Panel<T> = { data: T | null; error: string | null; loading: boolean };

  // Declared individually rather than via a `panel<T>()` factory: `$state` is a
  // compiler rune, not a function, and may only appear as a variable or class
  // field initialiser — it cannot be produced by a helper.
  const estate = $state<
    Panel<{
      services: Service[];
      portals: PortalRow[];
      project: string;
      region: string;
      managedProjects: string[];
      unreadable: string[];
    }>
  >({ data: null, error: null, loading: true });
  const notices = $state<Panel<{ incidents: Notice[]; recommendations: Rec[]; sources: Record<string, boolean> }>>({
    data: null, error: null, loading: true,
  });
  const releases = $state<Panel<{ sites: SiteReleases[] }>>({ data: null, error: null, loading: true });
  const errors = $state<Panel<{ projects: ErrRow[]; note?: string }>>({ data: null, error: null, loading: true });
  const perf = $state<Panel<{ days: number; services: Perf[]; sources: Record<string, boolean>; recommendation: string }>>({
    data: null, error: null, loading: true,
  });
  const cost = $state<Panel<Cost>>({ data: null, error: null, loading: true });
  const quota = $state<Panel<{ limits: Limit[] }>>({ data: null, error: null, loading: true });

  let busy = $state<string | null>(null);
  let toast = $state<{ kind: "ok" | "warn"; text: string } | null>(null);
  /** Rollback needs a dialog that names both releases — never a bare button. */
  let rollback = $state<{ site: string; name: string; from: Release | null; to: Release } | null>(null);

  async function fill<T>(p: Panel<T>, path: string) {
    p.loading = true;
    try {
      p.data = await api.get<T>(path);
      p.error = null;
    } catch (e) {
      p.error = e instanceof ApiError ? e.message : "Unavailable";
    } finally {
      p.loading = false;
    }
  }

  function loadAll() {
    void fill(estate, "/admin/estate");
    void fill(notices, "/admin/notices");
    void fill(releases, "/admin/releases");
    void fill(errors, "/errors/summary");
    void fill(perf, "/perf?days=7");
    void fill(cost, "/cost?days=30");
    void fill(quota, "/quota");
  }

  $effect(() => loadAll());

  const perfById = $derived(new Map((perf.data?.services ?? []).map((s) => [s.id, s])));

  /**
   * Services grouped by project. The estate turned out to be 55-odd services
   * across five projects — the HA Portal's function fleet alone is 48 — so one
   * flat table would be unreadable. Grouped, and collapsed by default for
   * anything but Fabric's own project, because you open this screen to check one
   * thing rather than to scroll past four dozen functions.
   */
  const grouped = $derived.by(() => {
    const m = new Map<string, Service[]>();
    for (const s of estate.data?.services ?? []) {
      if (!m.has(s.project)) m.set(s.project, []);
      m.get(s.project)!.push(s);
    }
    return [...m.entries()]
      .map(([project, services]) => ({ project, services: services.sort((a, b) => a.id.localeCompare(b.id)) }))
      .sort((a, b) => a.project.localeCompare(b.project));
  });

  let openProjects = $state<Set<string>>(new Set(["steyn-fabric"]));
  function toggleProject(p: string) {
    const next = new Set(openProjects);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    openProjects = next;
  }

  async function setScaling(s: Service, min: number, max: number) {
    busy = s.id;
    try {
      const ok = await stepUp.guard(
        // `confirm` is threaded through rather than captured: on a protected
        // project the guard re-runs THIS closure with whatever was typed, so the
        // retry has to be able to carry it.
        (confirm) =>
          api.patch(`/admin/services/${s.id}`, {
            minInstances: min,
            maxInstances: max,
            project: s.project,
            region: s.region,
            confirm,
          }),
        min === 1 ? `Keeping ${s.id} warm` : `Scaling ${s.id} to zero`,
      );
      toast = ok
        ? { kind: "ok", text: `${s.id} now min ${min} / max ${max}.` }
        : { kind: "warn", text: "Cancelled — scaling unchanged." };
      if (ok) await fill(estate, "/admin/estate");
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not change scaling" };
    } finally {
      busy = null;
    }
  }

  async function rollbackRevision(s: Service) {
    // The previous *ready* revision is not knowable from this payload — only the
    // latest and what is serving. Sending traffic to the latest ready revision is
    // the honest inverse: it undoes a manual pin, and a true version rollback is
    // the Hosting one below.
    const target = s.latestRevision;
    if (!target) return;
    busy = s.id;
    try {
      const ok = await stepUp.guard(
        (confirm) =>
          api.patch(`/admin/services/${s.id}/traffic`, {
            revision: target,
            project: s.project,
            region: s.region,
            confirm,
          }),
        `Sending all traffic on ${s.id} to ${target}`,
      );
      toast = ok ? { kind: "ok", text: `${s.id} serving ${target}.` } : { kind: "warn", text: "Cancelled." };
      if (ok) await fill(estate, "/admin/estate");
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not move traffic" };
    } finally {
      busy = null;
    }
  }

  async function toggle(p: PortalRow) {
    busy = p.id;
    try {
      const ok = await stepUp.guard(
        () => api.patch(`/admin/portals/${p.id}/enabled`, { enabled: !p.enabled }),
        `Switching ${p.name} ${p.enabled ? "off" : "on"}`,
      );
      toast = ok
        ? { kind: "ok", text: `${p.name} ${p.enabled ? "switched off" : "switched on"}.` }
        : { kind: "warn", text: "Cancelled — nothing changed." };
      if (ok) await fill(estate, "/admin/estate");
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not switch" };
    } finally {
      busy = null;
    }
  }

  async function doRollback() {
    if (!rollback) return;
    const { site, to, name } = rollback;
    rollback = null;
    busy = site;
    try {
      const ok = await stepUp.guard(
        () => api.post(`/admin/releases/${site}/rollback`, { version: to.version }),
        `Rolling ${name} back`,
      );
      toast = ok
        ? { kind: "ok", text: `${name} rolled back to the ${to.at ? dateZA(to.at) : "previous"} release.` }
        : { kind: "warn", text: "Cancelled — the live site is unchanged." };
      if (ok) await fill(releases, "/admin/releases");
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not roll back" };
    } finally {
      busy = null;
    }
  }

  const money = (v: number | null, ccy: string) => (v == null ? "—" : `${ccy === "USD" ? "$" : ccy + " "}${v.toFixed(2)}`);
  const fig = (v: number | null) => (v == null ? "—" : v.toLocaleString("en-ZA"));

  /** Which cost dimension the drilldown is showing. */
  let costView = $state<"project" | "portal" | "service">("project");
  /** Expanded row in the drilldown; shows that group's services and days. */
  let costOpen = $state<string | null>(null);

  const costGroups = $derived.by((): CostGroup[] => {
    const c = cost.data;
    if (!c) return [];
    if (costView === "project") return c.byProject;
    if (costView === "service") return c.byService;
    return c.byPortal.map((p) => ({ name: p.portal, monthToDate: p.cost, projected: p.projected, share: p.share }));
  });

  /**
   * Rows behind one group, aggregated by the *other* two dimensions. Drilling
   * into a project shows which portals and services inside it spend; drilling
   * into a service shows which projects use it. Done client-side from the rows
   * the API already returned, so a drilldown costs no extra BigQuery scan —
   * which matters because billing export is billed per byte scanned.
   */
  function costDetail(groupName: string): { label: string; cost: number }[] {
    const c = cost.data;
    if (!c) return [];
    const month = new Date().toISOString().slice(0, 7);
    const key = costView === "project" ? "project" : costView === "portal" ? "portal" : "service";
    const inner = costView === "service" ? "project" : "service";
    const m = new Map<string, number>();
    for (const r of c.rows) {
      if (!r.day.startsWith(month) || r[key] !== groupName) continue;
      m.set(r[inner], (m.get(r[inner]) ?? 0) + r.cost);
    }
    return [...m.entries()]
      .map(([label, v]) => ({ label, cost: Math.round(v * 100) / 100 }))
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 10);
  }
</script>

<div class="wrap">
  {#if toast}
    <div class="attn" class:attn--warn={toast.kind === "warn"} role="status">
      <span class="attn__badge" aria-hidden="true"><Icon name={toast.kind === "warn" ? "alert" : "check"} size={12} /></span>
      <span class="attn__text">{toast.text}</span>
    </div>
  {/if}

  <!-- ── Cost ──────────────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec">
      <span class="kicker">Spend</span>
      <span class="sub">budget {cost.data ? money(cost.data.budget, cost.data.currency) : "—"} / month</span>
    </div>
    {#if cost.loading}
      <p class="sub pad">Loading…</p>
    {:else if cost.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {cost.error}</p>
    {:else if cost.data}
      <div class="figs">
        <div class="fig">
          <div class="big">{money(cost.data.monthToDate, cost.data.currency)}</div>
          <div class="lb">month to date</div>
        </div>
        <div class="fig">
          <div class="big" class:over={cost.data.overBudget}>{money(cost.data.projectedMonthEnd, cost.data.currency)}</div>
          <div class="lb">projected month end</div>
        </div>
      </div>
      {#if cost.data.overBudget}
        <p class="sub foot"><Icon name="alert" size={12} /> The projection exceeds the budget.</p>
      {:else if !cost.data.projectable && cost.data.monthToDate !== null}
        <!-- Day 1 of a month is a few hours of usage; multiplying it by 31 would
             be a number, not a forecast. -->
        <p class="sub foot"><Icon name="pause" size={12} /> Too early in the month to project.</p>
      {/if}

      {#if cost.data.note}
        <!-- Honest placeholder rather than a zero: a fabricated figure here would
             be read as "it costs nothing", which is the wrong conclusion. -->
        <p class="sub foot"><Icon name="pause" size={12} /> {cost.data.note}</p>
      {:else}
        <div class="tabs">
          {#each [["project", "By project"], ["portal", "By portal"], ["service", "By service"]] as [id, label] (id)}
            <button class="tab" class:tab--on={costView === id} onclick={() => { costView = id as typeof costView; costOpen = null; }}>
              {label}
            </button>
          {/each}
        </div>

        {#if costGroups.length === 0}
          <p class="sub pad"><Icon name="pause" size={12} /> No spend recorded in this window.</p>
        {:else}
          {#each costGroups as g (g.name)}
            <div class="cg">
              <button class="cg__head" onclick={() => (costOpen = costOpen === g.name ? null : g.name)} aria-expanded={costOpen === g.name}>
                <Icon name={costOpen === g.name ? "chevron-down" : "chevron-right"} size={12} />
                <span class="cg__n">{g.name}</span>
                <span class="bar bar--inline"><span class="bar__fill" style="width:{g.share}%"></span></span>
                <span class="sub num">{g.share}%</span>
                <span class="num cg__v">{money(g.monthToDate, cost.data.currency)}</span>
                <span class="sub num cg__p">→ {money(g.projected, cost.data.currency)}</span>
              </button>
              {#if costOpen === g.name}
                <div class="cg__body">
                  {#each costDetail(g.name) as d (d.label)}
                    <div class="cg__row sub">
                      <span class="cg__rl">{d.label}</span>
                      <span class="num">{money(d.cost, cost.data.currency)}</span>
                    </div>
                  {/each}
                </div>
              {/if}
            </div>
          {/each}
          <p class="sub foot">
            Month to date, then the straight-line projection to month end. Drill in for the breakdown —
            computed from rows already fetched, so it costs no extra BigQuery scan.
          </p>
        {/if}
      {/if}
    {/if}
  </section>

  <!-- ── Notices ───────────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec">
      <span class="kicker">From Google Cloud</span>
      <button class="lnk" onclick={() => fill(notices, "/admin/notices")}>Refresh</button>
    </div>
    {#if notices.loading}
      <p class="sub pad">Checking…</p>
    {:else if notices.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {notices.error}</p>
    {:else if notices.data}
      {@const n = notices.data}
      {#if n.incidents.length === 0 && n.recommendations.length === 0}
        <p class="sub pad">
          <Icon name="check" size={12} /> Nothing outstanding.
          {#if !n.sources.serviceHealth}<em>(service health could not be reached)</em>{/if}
        </p>
      {:else}
        {#each n.incidents as i (i.id)}
          <div class="row row--warn">
            <Icon name="alert" size={14} />
            <div>
              <div class="row__t">{i.title}</div>
              <div class="sub">{i.state ?? ""} · {i.updateTime ? dateZA(i.updateTime) : ""}</div>
            </div>
          </div>
        {/each}
        {#each n.recommendations as r (r.recommender + r.description)}
          <div class="row">
            <Icon name="dot" size={14} />
            <div>
              <div class="row__t">{r.description}</div>
              <div class="sub">{r.recommender.split(".").slice(-1)[0]} · {r.priority ?? ""}</div>
            </div>
          </div>
        {/each}
      {/if}
    {/if}
  </section>

  <!-- ── Services + scaling + perf ─────────────────────────────────────── -->
  <section class="card">
    <div class="sec">
      <span class="kicker">Services</span>
      <span class="sub">
        {estate.data?.services.length ?? 0} across {grouped.length} project{grouped.length === 1 ? "" : "s"} ·
        {perf.data?.days ?? 7}-day figures
      </span>
    </div>
    {#if estate.loading}
      <p class="sub pad">Loading…</p>
    {:else if estate.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {estate.error}</p>
    {:else if estate.data}
      {#if estate.data.unreadable.length > 0}
        <!-- Named, so "no services" and "no access" never look the same. -->
        <p class="sub foot">
          <Icon name="alert" size={12} />
          Could not read {estate.data.unreadable.join(", ")} — grant the runtime service account
          <code>roles/run.viewer</code> there.
        </p>
      {/if}

      {#each grouped as g (g.project)}
        <button class="pgroup" onclick={() => toggleProject(g.project)} aria-expanded={openProjects.has(g.project)}>
          <Icon name={openProjects.has(g.project) ? "chevron-down" : "chevron-right"} size={14} />
          <span class="pgroup__n">{g.project}</span>
          <span class="sub">{g.services.length} service{g.services.length === 1 ? "" : "s"}</span>
          {#if g.services.some((s) => !s.ready)}
            <span class="status status--warn"><Icon name="alert" size={12} /> {g.services.filter((s) => !s.ready).length} not ready</span>
          {/if}
          {#if g.services.some((s) => s.minInstances > 0)}
            <span class="status status--warn"><Icon name="bolt" size={12} /> {g.services.filter((s) => s.minInstances > 0).length} billing warm</span>
          {/if}
        </button>

      {#if openProjects.has(g.project)}
      <div class="scroll">
        <table>
          <thead>
            <tr>
              <th>Service</th><th>State</th><th>Serving</th><th>Scaling</th>
              <th>Requests</th><th>p50 · p95</th><th>Cold starts</th><th></th>
            </tr>
          </thead>
          <tbody>
            {#each g.services as s (s.project + s.id)}
              {@const m = perfById.get(s.id)}
              <tr>
                <td>
                  <div class="nm">{s.id}</div>
                  <div class="sub num">{s.region}</div>
                  {#if !s.portalLabel}
                    <!-- Item 16: unlabelled spend cannot be attributed to a portal. -->
                    <div class="sub warnish"><Icon name="alert" size={10} /> no portal label</div>
                  {/if}
                </td>
                <td>
                  {#if s.ready}
                    <span class="status status--ok"><Icon name="check" size={12} /> ready</span>
                  {:else}
                    <span class="status status--warn"><Icon name="alert" size={12} /> not ready</span>
                  {/if}
                </td>
                <td class="sub num">
                  {#if s.serving.length === 0}
                    {s.latestRevision ?? "—"}
                  {:else}
                    {#each s.serving as t (t.revision)}<div>{t.revision} {t.percent}%</div>{/each}
                  {/if}
                </td>
                <td class="num sub">min {s.minInstances} / max {s.maxInstances ?? "—"}</td>
                <td class="num sub">{fig(m?.requests ?? null)}</td>
                <td class="num sub">{fig(m?.p50 ?? null)} · {fig(m?.p95 ?? null)}</td>
                <td class="num sub">{fig(m?.coldStarts ?? null)}</td>
                <td class="acts">
                  <!-- min-instances 1 keeps a container warm: no cold start, but
                       it bills continuously. The server caps it at 1. -->
                  <button
                    class="btn-primary"
                    disabled={busy === s.id || s.minInstances === 1}
                    onclick={() => setScaling(s, 1, s.maxInstances ?? 2)}>Keep warm</button>
                  <button
                    class="lnk"
                    disabled={busy === s.id || s.minInstances === 0}
                    onclick={() => setScaling(s, 0, s.maxInstances ?? 2)}>Scale to zero</button>
                  <button
                    class="lnk"
                    disabled={busy === s.id || !s.latestRevision}
                    onclick={() => rollbackRevision(s)}>Serve latest</button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      {/if}
      {/each}
      <p class="sub foot">
        <Icon name="alert" size={12} />
        {perf.data?.recommendation ??
          "“Keep warm” holds one instance running and bills continuously — it removes cold starts but is the one setting here that costs money."}
      </p>
      {#if perf.data && !perf.data.sources.coldStarts}
        <p class="sub foot"><Icon name="pause" size={12} /> Cold-start figures need the Monitoring API; they read as — until it answers.</p>
      {/if}
    {/if}
  </section>

  <!-- ── Headroom ──────────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec"><span class="kicker">Headroom</span><span class="sub">the limits that actually bind</span></div>
    {#if quota.loading}
      <p class="sub pad">Loading…</p>
    {:else if quota.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {quota.error}</p>
    {:else if quota.data}
      {#each quota.data.limits as l (l.id)}
        <div class="lim">
          <div class="lim__top">
            <span class="lim__t">{l.label}</span>
            <span class="sub num">
              {l.used == null ? "—" : l.used.toLocaleString("en-ZA")}{l.limit == null ? "" : ` / ${l.limit.toLocaleString("en-ZA")}`}
              {l.unit}
            </span>
          </div>
          <!-- No bar when the figure is unknown: an empty bar reads as "plenty
               left", which is a guess dressed up as a measurement. -->
          {#if l.pct !== null}
            <div class="bar"><span class="bar__fill" class:bar__fill--hot={l.pct >= 80} style="width:{Math.min(l.pct, 100)}%"></span></div>
          {/if}
          <div class="sub">{l.note}</div>
        </div>
      {/each}
    {/if}
  </section>

  <!-- ── Portal on/off ─────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec"><span class="kicker">Portals</span><span class="sub">switch off to hide everywhere</span></div>
    {#if estate.data}
      <div class="scroll">
        <table>
          <thead><tr><th>Portal</th><th>Project</th><th>State</th><th></th></tr></thead>
          <tbody>
            {#each estate.data.portals as p (p.id)}
              <tr class:off={!p.enabled}>
                <td class="nm">{p.name}</td>
                <td class="sub num">{p.projectId}</td>
                <td>
                  {#if p.enabled}
                    <span class="status status--ok"><Icon name="check" size={12} /> on</span>
                  {:else}
                    <span class="status status--idle"><Icon name="pause" size={12} /> off</span>
                  {/if}
                </td>
                <td class="acts">
                  <button class="lnk" disabled={busy === p.id} onclick={() => toggle(p)}>
                    {p.enabled ? "Switch off" : "Switch on"}
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      <p class="sub foot">
        Switching off hides the tile and stops the health sweep probing it. It does not tear down
        infrastructure — that stays a deliberate act at the console.
      </p>
    {/if}
  </section>

  <!-- ── Errors ────────────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec"><span class="kicker">Errors · 7 days</span></div>
    {#if errors.loading}
      <p class="sub pad">Loading…</p>
    {:else if errors.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {errors.error}</p>
    {:else if errors.data}
      {#if errors.data.projects.length === 0}
        <p class="sub pad"><Icon name="check" size={12} /> {errors.data.note ?? "No errors logged."}</p>
      {:else}
        <div class="scroll">
          <table>
            <thead><tr><th>Project</th><th>Errors</th><th>Last</th></tr></thead>
            <tbody>
              {#each errors.data.projects as e (e.project)}
                <tr>
                  <td class="nm">{e.project}</td>
                  <td class="num">{e.errors}</td>
                  <td class="sub num">{e.lastAt ? dateZA(e.lastAt) : "—"}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    {/if}
  </section>

  <!-- ── Deploy history + rollback ─────────────────────────────────────── -->
  <section class="card">
    <div class="sec"><span class="kicker">Recent deploys</span><span class="sub">newest first</span></div>
    {#if releases.loading}
      <p class="sub pad">Loading…</p>
    {:else if releases.error}
      <!-- Never render a rollback button that would silently no-op. -->
      <p class="sub pad"><Icon name="alert" size={12} /> {releases.error} — rollback is unavailable.</p>
    {:else if releases.data}
      {#each releases.data.sites as s (s.site)}
        <div class="rel">
          <div class="rel__h">{s.name} <span class="sub num">{s.site}</span></div>
          {#each s.releases.slice(0, 3) as r, i (r.version + String(r.at))}
            <div class="rel__r">
              <span class="sub num">{r.at ? dateZA(r.at) : "—"}</span>
              <span class="sub">· {r.type} · {r.by ?? "—"}</span>
              {#if r.sha}<span class="sub num">· {r.sha}</span>{/if}
              {#if r.subject}<span class="rel__sub">{r.subject}</span>{/if}
              {#if r.runUrl}<a class="lnk" href={r.runUrl} target="_blank" rel="noreferrer">run</a>{/if}
              {#if i > 0 && r.version}
                <button
                  class="lnk"
                  disabled={busy === s.site}
                  onclick={() => (rollback = { site: s.site, name: s.name, from: s.releases[0] ?? null, to: r })}
                >Roll back to this</button>
              {/if}
            </div>
          {/each}
        </div>
      {/each}
    {/if}
  </section>
</div>

<!-- The dialog names both releases, current and target, because "roll back" with
     no subject is the kind of button you press twice and regret once. -->
{#if rollback}
  <div class="scrim" role="dialog" aria-modal="true" aria-labelledby="rb-t">
    <div class="card--hero box">
      <h2 id="rb-t">Roll {rollback.name} back?</h2>
      <p class="sub">
        Live now: <strong>{rollback.from?.at ? dateZA(rollback.from.at) : "the current release"}</strong>
        {#if rollback.from?.sha}<span class="num"> · {rollback.from.sha}</span>{/if}
      </p>
      <p class="sub">
        Rolling back to: <strong>{rollback.to.at ? dateZA(rollback.to.at) : rollback.to.version}</strong>
        {#if rollback.to.sha}<span class="num"> · {rollback.to.sha}</span>{/if}
      </p>
      <p class="sub note">This re-releases the older files. It does not change the repo, and it is recorded in the trail.</p>
      <div class="boxacts">
        <button class="lnk" onclick={() => (rollback = null)}>Cancel</button>
        <button class="btn-primary" onclick={doRollback}>Roll back</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .wrap { display: flex; flex-direction: column; gap: 14px; }
  .card { padding: 13px 15px 15px; }
  .sec { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; padding-bottom: 9px; }
  .pad { padding: 6px 0 2px; display: flex; align-items: center; gap: 6px; }
  .foot { margin: 9px 0 0; display: flex; align-items: flex-start; gap: 6px; }
  .scroll { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 0.7812rem; }
  th { text-align: left; font-size: 0.72rem; font-weight: 700; color: var(--mut); padding: 6px 8px;
       border-bottom: 1px solid var(--line); white-space: nowrap; }
  td { padding: 9px 8px; border-bottom: 1px solid var(--line); vertical-align: middle; }
  tbody tr:last-child td { border-bottom: none; }
  tr.off { opacity: 0.55; }
  .nm { font-weight: 700; color: var(--tx); }
  .warnish { color: var(--warn); display: inline-flex; align-items: center; gap: 4px; }
  .acts { display: flex; gap: 8px; align-items: center; justify-content: flex-end; flex-wrap: wrap; }
  .lnk { font-size: 0.72rem; font-weight: 700; color: var(--mut); }
  .lnk:hover { color: var(--acc); }
  button:disabled { opacity: 0.45; cursor: default; }
  .row { display: flex; gap: 9px; padding: 9px 10px; border-radius: var(--r-control);
         box-shadow: inset 0 0 0 1px var(--line); margin-bottom: 6px; align-items: flex-start; }
  .row--warn { border-left: 2px solid var(--warn); }
  .row__t { font-size: 0.7812rem; color: var(--tx); font-weight: 700; }
  .rel { padding: 8px 0; border-bottom: 1px solid var(--line); }
  .rel:last-child { border-bottom: none; }
  .rel__h { font-weight: 700; font-size: 0.7812rem; color: var(--tx); display: flex; gap: 8px; align-items: baseline; }
  .rel__r { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; padding: 3px 0 3px 2px; }
  .rel__sub { font-size: 0.72rem; color: var(--tx2); }

  .figs { display: flex; gap: 30px; flex-wrap: wrap; padding: 2px 0 4px; }
  .fig .big { font-size: 1.625rem; }
  .big.over { color: var(--warn); }

  .pgroup { display: flex; align-items: center; gap: 9px; width: 100%; padding: 9px 2px; text-align: left;
            border-top: 1px solid var(--line); }
  .pgroup:first-of-type { border-top: none; }
  .pgroup__n { font-size: 0.7812rem; font-weight: 800; color: var(--tx); }
  code { font-size: 0.72rem; color: var(--acc); }

  .lim { padding: 8px 0; border-bottom: 1px solid var(--line); }
  .lim:last-child { border-bottom: none; }
  .lim__top { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
  .lim__t { font-size: 0.7812rem; font-weight: 700; color: var(--tx); }
  .bar { height: 5px; margin: 6px 0 5px; border-radius: 3px; background: var(--fill); overflow: hidden; }
  .bar__fill { display: block; height: 100%; background: var(--ok); }
  .bar__fill--hot { background: var(--warn); }
  .bar--inline { flex: 1; min-width: 40px; max-width: 160px; margin: 0; }

  .tabs { display: flex; gap: 6px; padding: 2px 0 10px; }
  .tab { padding: 4px 10px; border-radius: var(--r-pill); font-size: 0.72rem; font-weight: 700;
         color: var(--mut); box-shadow: inset 0 0 0 1px var(--line); }
  .tab--on { color: var(--tx); background: var(--wash); box-shadow: inset 0 0 0 1px var(--acc); }

  .cg { border-bottom: 1px solid var(--line); }
  .cg:last-of-type { border-bottom: none; }
  .cg__head { display: flex; align-items: center; gap: 9px; width: 100%; padding: 8px 2px; text-align: left; }
  .cg__n { font-size: 0.7812rem; font-weight: 700; color: var(--tx); min-width: 120px; }
  .cg__v { font-size: 0.7812rem; color: var(--tx); font-weight: 700; }
  .cg__p { min-width: 74px; text-align: right; }
  .cg__body { padding: 2px 2px 10px 24px; }
  .cg__row { display: flex; justify-content: space-between; gap: 12px; padding: 2px 0; }
  .cg__rl { overflow-wrap: anywhere; }

  .scrim { position: fixed; inset: 0; z-index: 80; display: grid; place-items: center; padding: 24px;
           background: rgb(0 0 0 / 0.5); }
  .box { width: min(430px, 100%); padding: 20px; }
  .box h2 { margin: 0 0 10px; font-size: 0.9375rem; font-weight: 800; color: var(--tx); }
  .box .sub { margin: 3px 0; }
  .note { margin-top: 10px !important; }
  .boxacts { display: flex; align-items: center; justify-content: flex-end; gap: 14px; margin-top: 16px; }
</style>
